import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

function PostnatalVisitForm() {
  const { handleAPICall } = useAuth();
  const [formData, setFormData] = useState({
    patientId: '',
    visitDate: '',
    motherCondition: '',
    lochia: '',
    uterineInvolution: '',
    breastfeeding: '',
    babyWeight: '',
    babyLength: '',
    notes: '',
    apiType: 'postnatalVisits'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requiredFields = ['patientId', 'visitDate', 'motherCondition', 'babyWeight'];

  const validateField = (name, value) => {
    if (requiredFields.includes(name) && !value.trim()) return 'This field is required';
    if (name === 'babyWeight' && isNaN(value)) return 'Invalid weight value';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await handleAPICall(formData);
      // Reset form
      setFormData({
        patientId: '',
        visitDate: '',
        motherCondition: '',
        lochia: '',
        uterineInvolution: '',
        breastfeeding: '',
        babyWeight: '',
        babyLength: '',
        notes: '',
        apiType: 'postnatalVisits'
      });
    } catch (error) {
      console.error('Submission failed:', error);
    }
    setIsSubmitting(false);
  };

  return (
    <form 
      onSubmit={handleSubmit}
      className="p-5 bg-white rounded-4 shadow-lg mt-5"
      style={{ maxWidth: '', margin: '0 auto' }}
    >
      <h2 className="mb-2 text-primary text-center fw-bold">Postnatal Visit Record</h2>

      <div className="row g-4 mb-2">
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Patient *</label>
            <select
              name="patientId"
              value={formData.patientId}
              onChange={handleChange}
              className={`form-select border-bottom border-light py-2 w-100 ${errors.patientId ? 'is-invalid' : ''}`}
            >
              <option value="">Select Patient</option>
              <option value="1">Patient 1</option>
              <option value="2">Patient 2</option>
            </select>
            {errors.patientId && <div className="invalid-feedback d-block">{errors.patientId}</div>}
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Visit Date *</label>
            <input
              type="date"
              name="visitDate"
              value={formData.visitDate}
              onChange={handleChange}
              className={`form-control border-bottom border-light py-2 px-0 ${errors.visitDate ? 'is-invalid' : ''}`}
            />
            {errors.visitDate && <div className="invalid-feedback d-block">{errors.visitDate}</div>}
          </div>
        </div>
      </div>

      <div className="row g-4 mb-2">
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Mother's Condition *</label>
            <textarea
              name="motherCondition"
              value={formData.motherCondition}
              onChange={handleChange}
              className={`form-control bg-light border-0 rounded-3 p-3 ${errors.motherCondition ? 'is-invalid' : ''}`}
              rows="4"
            ></textarea>
            {errors.motherCondition && <div className="invalid-feedback d-block">{errors.motherCondition}</div>}
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Baby Weight (kg) *</label>
            <input
              type="number"
              name="babyWeight"
              step="0.01"
              value={formData.babyWeight}
              onChange={handleChange}
              className={`form-control border-bottom border-light py-2 px-0 ${errors.babyWeight ? 'is-invalid' : ''}`}
            />
            {errors.babyWeight && <div className="invalid-feedback d-block">{errors.babyWeight}</div>}
          </div>

          <div className="form-group mt-4">
            <label className="form-label text-muted mb-2">Baby Length (cm)</label>
            <input
              type="number"
              name="babyLength"
              step="0.1"
              value={formData.babyLength}
              onChange={handleChange}
              className="form-control border-bottom border-light py-2 px-0"
            />
          </div>
        </div>
      </div>

      <div className="row g-4 mb-2">
        <div className="col-md-4">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Uterine Involution</label>
            <input
              type="number"
              name="uterineInvolution"
              value={formData.uterineInvolution}
              onChange={handleChange}
              className="form-control border-bottom border-light py-2 px-0"
              placeholder="Enter measurement"
            />
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Lochia Description</label>
            <select
              name="lochia"
              value={formData.lochia}
              onChange={handleChange}
              className="form-select border-bottom border-light py-2 w-100"
            >
              <option value="">Select Lochia Type</option>
              <option value="rubra">Rubra</option>
              <option value="serosa">Serosa</option>
              <option value="alba">Alba</option>
            </select>
          </div>
        </div>

        <div className="col-md-4">
          <div className="form-group">
            <label className="form-label text-muted mb-2">Breastfeeding</label>
            <select
              name="breastfeeding"
              value={formData.breastfeeding}
              onChange={handleChange}
              className="form-select border-bottom border-light py-2 w-100"
            >
              <option value="">Select Feeding Type</option>
              <option value="exclusive">Exclusive</option>
              <option value="mixed">Mixed</option>
              <option value="formula">Formula</option>
            </select>
          </div>
        </div>
      </div>

      <div className="d-grid mt-5">
        <button 
          type="submit"
          className="btn btn-primary btn-lg py-3 fw-bold hover-effect w-100"
          disabled={!formData.patientId || !formData.visitDate || !formData.motherCondition || !formData.babyWeight || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Postnatal Record'}
        </button>
      </div>
    </form>
  );
}

export default PostnatalVisitForm;